import { useSelector } from 'react-redux';
import deepEqual from 'fast-deep-equal/es6/react';
import TrapTabs from '@/components/atoms/trap-tabs';
import { ApplicationState } from '@/store';
import { QueryKeys } from '@/types';
import { isAgentOrAgencyRole, isPartnerRole } from '@/utilities/roles/permissions';
import { TabIdStageMap, TabIdStageMapForAgent } from 'apply-data';
import { useNavigate } from 'react-router-dom';

export interface ISidebarTabs {
  activeTab: string;
}

export const SidebarTabs = ({ activeTab }: ISidebarTabs) => {
  const navigate = useNavigate();

  const { customTabs, tabsLoaded } = useSelector(
    ({ offersDashboard }: ApplicationState) => ({
      customTabs: offersDashboard.customTabs,
      tabsLoaded: offersDashboard.tabsLoaded,
    }),
    deepEqual,
  );
  const userRole = useSelector(({ auth }: ApplicationState) => auth.role);

  const params = new URLSearchParams(window.location.search);
  const isSearchActive = params.has(QueryKeys.SEARCH) && (params.get(QueryKeys.SEARCH) || '').length > 0;
  const filterByMessage = params.get(QueryKeys.UNREAD_MESSAGES) === 'true';

  /**
   * Callback that appends stages and page information on navigation
   * to a new tab.  Able to append multiple stages to the QSPs.
   */
  const onTabSelect = (id: string) => {
    const params = new URLSearchParams(window.location.search);
    params.set(QueryKeys.PAGE, '1');
    params.delete(QueryKeys.STAGE);
    params.delete(QueryKeys.APPLICATION_STAGE);
    params.delete(QueryKeys.OFFER_STAGE);

    const isAgentOrPartnerUser = isAgentOrAgencyRole(userRole) || isPartnerRole(userRole);

    const tabMap = isAgentOrPartnerUser ? TabIdStageMapForAgent : TabIdStageMap;

    // If the selected tab is not mapped, we do not perform the navigation.
    if (!(id in tabMap)) {
      console.warn(`Tab ID ${id} not found in TabIdStageMap`);
      return;
    }

    const tabMapForCurrentStatus = tabMap?.[id];
    const { applicationStatuses, offerStatuses } = tabMapForCurrentStatus ?? {};

    for (const status of applicationStatuses ?? []) {
      params.append(QueryKeys.APPLICATION_STAGE, status);
    }

    for (const status of offerStatuses ?? []) {
      params.append(QueryKeys.OFFER_STAGE, status);
    }

    params.set(QueryKeys.CURRENT_TAB, id);
    navigate({ search: params.toString() });
  };

  /**
   * Callback that checks if what tab is active based upon
   * comparison between the active tab array, and the statuses array.
   */
  const isActiveTab = (id: string) => {
    return activeTab === id;
  };

  if (tabsLoaded) {
    return Object.keys(customTabs).map((group, index) => {
      return (
        <TrapTabs
          key={`${group}-${index}`}
          tabs={customTabs[group]}
          onTabSelect={onTabSelect}
          label={group}
          activeTab={activeTab}
          isActiveTab={isActiveTab}
          isSearchActive={isSearchActive}
          filterByMessage={filterByMessage}
          dataSelector={group}
        />
      );
    });
  }
  return <></>;
};
